import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { SiteHeader } from "@/components/site-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Pagination } from "@/components/ui/pagination";
import { api, authorizedFetch } from "@/lib/api";
import { usePermissions } from "@/hooks/use-permissions";
import { PencilIcon, PlusIcon, SearchIcon, Trash2Icon } from "lucide-react";
import { RestoreIcon } from "@/components/icons/RestoreIcon";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:3001";
const PAGE_SIZE = 25;

interface FinalDestRow {
  id: string;
  name: string;
  code: string;
  isActive: boolean;
  sortOrder: number;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

type FilterStatus = "active" | "deleted" | "all";

type FormState = {
  name: string;
  code: string;
  sortOrder: string;
  isActive: boolean;
};

const EMPTY_FORM: FormState = {
  name: "",
  code: "",
  sortOrder: "0",
  isActive: true,
};

export default function FinalDestinationsPage() {
  const qc = useQueryClient();
  const { can } = usePermissions();

  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState<FilterStatus>("active");
  const [page, setPage] = useState(1);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [editing, setEditing] = useState<FinalDestRow | null>(null);
  const [deleting, setDeleting] = useState<FinalDestRow | null>(null);
  const [form, setForm] = useState<FormState>({ ...EMPTY_FORM });
  const [formError, setFormError] = useState<string | null>(null);
  const [restoringId, setRestoringId] = useState<string | null>(null);

  const { data: destinations = [], isLoading } = useQuery({
    queryKey: ["batea", "final-destinations", filterStatus],
    queryFn: async () => {
      const query: Record<string, string> = {};
      if (filterStatus === "active") query.deleted = "false";
      else if (filterStatus === "deleted") query.deleted = "true";
      const res = await api.batea["final-destinations"].$get({ query });
      if (!res.ok) throw new Error("Error cargando destinos finales");
      return res.json() as Promise<FinalDestRow[]>;
    },
  });

  const filtered = useMemo(() => {
    const term = search.toLowerCase().trim();
    return destinations.filter((item) => {
      if (!term) return true;
      return item.name.toLowerCase().includes(term);
    });
  }, [destinations, search]);

  useEffect(() => { setPage(1); }, [search, filterStatus]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const createMut = useMutation({
    mutationFn: async (data: FormState) => {
      const res = await api.batea["final-destinations"].$post({
        json: {
          name: data.name,
          code: data.code,
          sortOrder: Number(data.sortOrder) || 0,
          isActive: data.isActive,
        },
      });
      if (!res.ok) {
        const body = await res.json() as { error?: string };
        throw new Error(body.error ?? "Error al crear destino");
      }
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["batea", "final-destinations"] });
      closeSheet();
    },
    onError: (err: Error) => setFormError(err.message),
  });

  const updateMut = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: FormState }) => {
      const res = await api.batea["final-destinations"][":id"].$patch({
        param: { id },
        json: {
          name: data.name,
          code: data.code,
          sortOrder: Number(data.sortOrder) || 0,
          isActive: data.isActive,
        },
      });
      if (!res.ok) {
        const body = await res.json() as { error?: string };
        throw new Error(body.error ?? "Error al actualizar destino");
      }
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["batea", "final-destinations"] });
      closeSheet();
    },
    onError: (err: Error) => setFormError(err.message),
  });

  const deleteMut = useMutation({
    mutationFn: async (id: string) => {
      await api.batea["final-destinations"][":id"].$delete({ param: { id } });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["batea", "final-destinations"] });
      setDeleteOpen(false);
      setDeleting(null);
    },
    onError: (err: Error) => setFormError(err.message),
  });

  const restoreMut = useMutation({
    mutationFn: async (id: string) => {
      setRestoringId(id);
      await authorizedFetch(`${API_URL}/api/batea/final-destinations/${id}/restore`, { method: "PATCH" });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["batea", "final-destinations"] });
      setRestoringId(null);
    },
    onError: (err: Error) => {
      setFormError(err.message);
      setRestoringId(null);
    },
  });

  function closeSheet() {
    setSheetOpen(false);
    setEditing(null);
    setForm({ ...EMPTY_FORM });
    setFormError(null);
  }

  function openCreate() {
    setEditing(null);
    setForm({ ...EMPTY_FORM });
    setFormError(null);
    setSheetOpen(true);
  }

  function openEdit(item: FinalDestRow) {
    setEditing(item);
    setForm({ name: item.name, code: item.code, sortOrder: String(item.sortOrder), isActive: item.isActive });
    setFormError(null);
    setSheetOpen(true); // code is kept in form state for the PATCH payload but not shown
  }

  function openDelete(item: FinalDestRow) {
    setDeleting(item);
    setDeleteOpen(true);
  }

  function sanitizeCode(v: string) {
    return v.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 10);
  }

  function codeFromName(name: string) {
    return name.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 10);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);
    const name = form.name.trim();
    const payload: FormState = {
      name,
      code: codeFromName(name),
      sortOrder: form.sortOrder,
      isActive: form.isActive,
    };
    if (!payload.name) { setFormError("El nombre es requerido"); return; }
    if (!payload.code) { setFormError("El nombre no genera un código válido (usá letras o números)"); return; }
    if (editing) updateMut.mutate({ id: editing.id, data: payload });
    else createMut.mutate(payload);
  }

  const isSaving = createMut.isPending || updateMut.isPending;
  const canWrite = can("write", "batea");

  return (
    <>
      <SiteHeader title="Destinos finales" />
      <div className="flex flex-1 flex-col px-4 py-6 lg:px-6">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="relative flex-1 min-w-[220px]">
            <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por nombre..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8"
            />
          </div>

          <Select value={filterStatus} onValueChange={(v) => setFilterStatus(v as FilterStatus)}>
            <SelectTrigger className="w-40" size="sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Activos</SelectItem>
              <SelectItem value="deleted">Eliminados</SelectItem>
              <SelectItem value="all">Todos</SelectItem>
            </SelectContent>
          </Select>

          {canWrite && (
            <Button onClick={openCreate} className="gap-2">
              <PlusIcon className="size-4" /> Nuevo destino
            </Button>
          )}
        </div>

        <div className="rounded-lg border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead className="w-20 text-center">Orden</TableHead>
                <TableHead className="w-20 text-center">Estado</TableHead>
                {canWrite && <TableHead className="w-9" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={canWrite ? 4 : 3} className="text-center py-16 text-muted-foreground">
                    Cargando destinos finales...
                  </TableCell>
                </TableRow>
              ) : paged.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canWrite ? 4 : 3} className="text-center py-16 text-muted-foreground">
                    {search ? "Sin resultados para la búsqueda" : "No hay destinos finales cargados"}
                  </TableCell>
                </TableRow>
              ) : (
                paged.map((item) => (
                  <TableRow key={item.id} className={item.deletedAt ? "opacity-60" : undefined}>
                    <TableCell className="font-medium text-sm">{item.name}</TableCell>
                    <TableCell className="text-center text-sm text-muted-foreground">{item.sortOrder}</TableCell>
                    <TableCell className="text-center">
                      {item.isActive && !item.deletedAt
                        ? <span className="text-[10px] px-1.5 py-0 rounded-full bg-emerald-100 text-emerald-700 font-medium">Activo</span>
                        : <span className="text-[10px] px-1.5 py-0 rounded-full bg-muted text-muted-foreground font-medium">Inactivo</span>}
                    </TableCell>
                    {canWrite && (
                      <TableCell className="w-9 pr-2">
                        <div className="flex justify-end gap-1">
                          {item.deletedAt ? (
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              onClick={() => restoreMut.mutate(item.id)}
                              disabled={restoringId === item.id}
                              className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                              title="Restaurar"
                            >
                              <RestoreIcon className="size-3.5" />
                            </Button>
                          ) : (
                            <>
                              <Button variant="ghost" size="icon-sm" onClick={() => openEdit(item)} title="Editar">
                                <PencilIcon className="size-3.5" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon-sm"
                                onClick={() => openDelete(item)}
                                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                title="Eliminar"
                              >
                                <Trash2Icon className="size-3.5" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            totalItems={filtered.length}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
          />
        </div>
      </div>

      {/* ── Sheet create/edit ── */}
      <Sheet open={sheetOpen} onOpenChange={(open) => { if (!open) closeSheet(); else setSheetOpen(true); }}>
        <SheetContent side="right" className="sm:max-w-sm flex flex-col">
          <SheetHeader className="p-4">
            <SheetTitle>{editing ? "Editar destino final" : "Nuevo destino final"}</SheetTitle>
            <SheetDescription>
              Los destinos son seleccionables por el chofer en la app móvil durante el viaje batea.
            </SheetDescription>
          </SheetHeader>

          <form onSubmit={handleSubmit} className="flex flex-col flex-1">
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
              <div className="grid gap-1.5">
                <Label htmlFor="fd-name">Nombre <span className="text-destructive">*</span></Label>
                <Input
                  id="fd-name"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  maxLength={200}
                  placeholder="Laje 20"
                  required
                />
              </div>

              <div className="grid gap-1.5">
                <Label htmlFor="fd-sort">Orden de visualización</Label>
                <Input
                  id="fd-sort"
                  type="number"
                  min={0}
                  value={form.sortOrder}
                  onChange={(e) => setForm((f) => ({ ...f, sortOrder: e.target.value }))}
                  placeholder="0"
                />
                <p className="text-xs text-muted-foreground">Número menor aparece primero en el selector del chofer.</p>
              </div>

              <div className="flex items-center gap-3">
                <input
                  id="fd-active"
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
                  className="size-4 rounded border-border"
                />
                <Label htmlFor="fd-active" className="cursor-pointer">Activo (visible en la app del chofer)</Label>
              </div>

              {formError && (
                <p className="text-sm text-destructive bg-destructive/10 rounded px-3 py-2">{formError}</p>
              )}
            </div>

            <SheetFooter className="p-4 border-t">
              <Button type="button" variant="outline" onClick={closeSheet}>Cancelar</Button>
              <Button type="submit" disabled={isSaving}>
                {isSaving ? "Guardando..." : editing ? "Guardar cambios" : "Crear destino"}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>

      {/* ── Delete dialog ── */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>Eliminar destino final</DialogTitle>
            <DialogDescription>
              ¿Eliminás el destino <strong>{deleting?.name}</strong>?
              Esta acción es reversible desde la opción de restaurar.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose>
              <Button variant="outline" onClick={() => setDeleting(null)}>Cancelar</Button>
            </DialogClose>
            <Button
              variant="destructive"
              disabled={deleteMut.isPending}
              onClick={() => deleting && deleteMut.mutate(deleting.id)}
            >
              {deleteMut.isPending ? "Eliminando..." : "Eliminar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
